import type { AgentRuntimeSnapshot } from "@cord/shared"
import type { ThreadSyncReplicaQuality } from "@/lib/thread-sync-runtime-materialization"
import type { ThreadSyncReplicaProvenance } from "@/lib/thread-sync-replica-provenance"

export interface ThreadSyncReplicaSummary {
  cursor: number | null
  quality: ThreadSyncReplicaQuality | null
  savedAtMs: number | null
  provenance: ThreadSyncReplicaProvenance | null
}

const THREAD_SYNC_REPLICA_QUALITY_PRIORITY_CONTINUITY_VALIDATED = 2
const THREAD_SYNC_REPLICA_QUALITY_PRIORITY_BEST_EFFORT = 1
const THREAD_SYNC_REPLICA_QUALITY_PRIORITY_UNKNOWN = 0

const THREAD_SYNC_REPLICA_SOURCE_PRIORITY_FOREGROUND_WS = 4
const THREAD_SYNC_REPLICA_SOURCE_PRIORITY_FOREGROUND_HTTP_PREFILL = 3
const THREAD_SYNC_REPLICA_SOURCE_PRIORITY_BACKGROUND_HTTP = 2
const THREAD_SYNC_REPLICA_SOURCE_PRIORITY_HYDRATED = 1
const THREAD_SYNC_REPLICA_SOURCE_PRIORITY_UNKNOWN = 0

function normalizeNonNegativeInteger(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
    return null
  }

  return Math.floor(value)
}

function resolveThreadSyncReplicaQualityPriority(
  quality: ThreadSyncReplicaQuality | null | undefined,
): number {
  if (quality === "continuity_validated") {
    return THREAD_SYNC_REPLICA_QUALITY_PRIORITY_CONTINUITY_VALIDATED
  }

  if (quality === "best_effort") {
    return THREAD_SYNC_REPLICA_QUALITY_PRIORITY_BEST_EFFORT
  }

  return THREAD_SYNC_REPLICA_QUALITY_PRIORITY_UNKNOWN
}

function resolveThreadSyncReplicaSourcePriority(
  provenance: ThreadSyncReplicaProvenance | null | undefined,
): number {
  switch (provenance?.sourceKind) {
    case "foreground_ws":
      return THREAD_SYNC_REPLICA_SOURCE_PRIORITY_FOREGROUND_WS
    case "foreground_http_prefill":
      return THREAD_SYNC_REPLICA_SOURCE_PRIORITY_FOREGROUND_HTTP_PREFILL
    case "background_http":
      return THREAD_SYNC_REPLICA_SOURCE_PRIORITY_BACKGROUND_HTTP
    case "hydrated":
      return THREAD_SYNC_REPLICA_SOURCE_PRIORITY_HYDRATED
    default:
      return THREAD_SYNC_REPLICA_SOURCE_PRIORITY_UNKNOWN
  }
}

export function resolveThreadSyncReplicaCursor(args: {
  resumeCursor?: number | null
  snapshot?: AgentRuntimeSnapshot | null
}): number | null {
  const resumeCursor = normalizeNonNegativeInteger(args.resumeCursor)
  if (resumeCursor !== null) {
    return resumeCursor
  }

  return normalizeNonNegativeInteger(args.snapshot?.cursorState.providerEventId)
}

export function buildThreadSyncReplicaSummary(args: {
  resumeCursor?: number | null
  snapshot?: AgentRuntimeSnapshot | null
  quality: ThreadSyncReplicaQuality | null
  savedAtMs: number | null
  provenance: ThreadSyncReplicaProvenance | null
}): ThreadSyncReplicaSummary {
  return {
    cursor: resolveThreadSyncReplicaCursor({
      resumeCursor: args.resumeCursor,
      snapshot: args.snapshot,
    }),
    quality: args.quality,
    savedAtMs: normalizeNonNegativeInteger(args.savedAtMs),
    provenance: args.provenance,
  }
}

function compareThreadSyncReplicaSummaryCursors(args: {
  current: ThreadSyncReplicaSummary
  incoming: ThreadSyncReplicaSummary
}): "incoming" | "current" | "equal" {
  const currentCursor = args.current.cursor ?? -1
  const incomingCursor = args.incoming.cursor ?? -1
  if (incomingCursor > currentCursor) {
    return "incoming"
  }

  if (incomingCursor < currentCursor) {
    return "current"
  }

  return "equal"
}

export function compareThreadSyncReplicaSummaries(args: {
  current: ThreadSyncReplicaSummary | null
  incoming: ThreadSyncReplicaSummary | null
}): "incoming" | "current" | "equal" {
  const current = args.current
  const incoming = args.incoming

  if (current === null) {
    return args.incoming === null ? "equal" : "incoming"
  }

  if (incoming === null) {
    return "current"
  }

  const cursorComparison = compareThreadSyncReplicaSummaryCursors({
    current,
    incoming,
  })
  if (cursorComparison !== "equal") {
    return cursorComparison
  }

  const currentQualityPriority = resolveThreadSyncReplicaQualityPriority(
    current.quality,
  )
  const incomingQualityPriority = resolveThreadSyncReplicaQualityPriority(
    incoming.quality,
  )
  if (incomingQualityPriority > currentQualityPriority) {
    return "incoming"
  }

  if (incomingQualityPriority < currentQualityPriority) {
    return "current"
  }

  const currentSourcePriority = resolveThreadSyncReplicaSourcePriority(
    current.provenance,
  )
  const incomingSourcePriority = resolveThreadSyncReplicaSourcePriority(
    incoming.provenance,
  )
  if (incomingSourcePriority > currentSourcePriority) {
    return "incoming"
  }

  if (incomingSourcePriority < currentSourcePriority) {
    return "current"
  }

  const currentSavedAtMs = current.savedAtMs ?? 0
  const incomingSavedAtMs = incoming.savedAtMs ?? 0
  if (incomingSavedAtMs > currentSavedAtMs) {
    return "incoming"
  }

  if (incomingSavedAtMs < currentSavedAtMs) {
    return "current"
  }

  return "equal"
}

export function doesIncomingThreadSyncReplicaWin(args: {
  current: ThreadSyncReplicaSummary | null
  incoming: ThreadSyncReplicaSummary | null
}): boolean {
  return compareThreadSyncReplicaSummaries(args) === "incoming"
}
